import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminTemplates.css';

const API_BASE =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  'https://localhost:7038';

const initialForm = {
  name: '',
  slug: '',
  description: '',
  tags: '',
  recommendedForIndustries: '',
  accentColor: '#0d9488',
  sidebarBgColor: '#0A7567',
  layout: 'sidebar-left',
  sortOrder: 0,
  isActive: true,
};

const parseStyleConfig = (template) => {
  try {
    return JSON.parse(template?.styleConfigJson || '{}') || {};
  } catch {
    return {};
  }
};

const buildSlug = (value = '') =>
  value
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-');

export default function AdminTemplates() {
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [thumbnail, setThumbnail] = useState(null);
  const [templateFile, setTemplateFile] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const navigate = useNavigate();

  const token = localStorage.getItem('adminToken');

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
    }
  }, [token, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setMsg('');
    try {
      const r = await fetch(`${API_BASE}/api/resume-builder/templates`, {
        headers,
      });

      if (r.status === 401) {
        navigate('/admin/login');
        return;
      }

      const data = await r.json();
      setTemplates(Array.isArray(data) ? data : data.templates ?? []);
    } catch (error) {
      console.error(error);
      setMsg('Network error while loading templates.');
    } finally {
      setLoading(false);
    }
  }, [headers, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setEditing(null);
    setForm(initialForm);
    setThumbnail(null);
    setTemplateFile(null);
    setMsg('');
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const save = async (event) => {
    event.preventDefault();

    if (!form.name.trim()) {
      setMsg('Template name is required.');
      return;
    }

    if (!editing && !templateFile) {
      setMsg('Template DOCX file is required.');
      return;
    }

    const finalSlug = form.slug?.trim() ? buildSlug(form.slug) : buildSlug(form.name);

    setSaving(true);
    setMsg('');

    const fd = new FormData();
    fd.append('name', form.name.trim());
    fd.append('slug', finalSlug);
    fd.append('description', form.description || '');
    fd.append('accentColor', form.accentColor || '#0d9488');
    fd.append('sidebarBgColor', form.sidebarBgColor || '#0A7567');
    fd.append('layout', form.layout || 'sidebar-left');
    fd.append('sortOrder', String(Number(form.sortOrder || 0)));
    fd.append('isActive', String(Boolean(form.isActive)));
    fd.append('recommendedForIndustries', form.recommendedForIndustries || '');

    const tags = (form.tags || '')
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean);

    tags.forEach((tag) => fd.append('tags', tag));

    if (thumbnail) fd.append('thumbnail', thumbnail);
    if (templateFile) fd.append('templateFile', templateFile);

    const url = editing
      ? `${API_BASE}/api/admin/templates/${editing}`
      : `${API_BASE}/api/admin/templates/upload`;

    const method = editing ? 'PUT' : 'POST';

    try {
      const r = await fetch(url, {
        method,
        headers,
        body: fd,
      });

      if (r.status === 401) {
        navigate('/admin/login');
        return;
      }

      if (!r.ok) {
        const payload = await r.json().catch(() => null);
        setMsg(payload?.message || payload?.title || 'Error saving template.');
        return;
      }

      setMsg(editing ? 'Template updated successfully.' : 'Template created successfully.');
      resetForm();
      await load();
    } catch (error) {
      console.error(error);
      setMsg('Network error while saving template.');
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (id, name) => {
    if (!window.confirm(`Delete template "${name}"?`)) return;

    try {
      const r = await fetch(`${API_BASE}/api/admin/templates/${id}`, {
        method: 'DELETE',
        headers,
      });

      if (r.status === 401) {
        navigate('/admin/login');
        return;
      }

      if (!r.ok) {
        setMsg('Could not delete template.');
        return;
      }

      setMsg('Template deleted successfully.');
      await load();
    } catch (error) {
      console.error(error);
      setMsg('Network error while deleting template.');
    }
  };

  const editTemplate = (template) => {
    const style = parseStyleConfig(template);

    setEditing(template.id);
    setForm({
      name: template.name || '',
      slug: template.slug || '',
      description: template.description || '',
      tags: Array.isArray(template.tags) ? template.tags.join(', ') : '',
      recommendedForIndustries: template.recommendedForIndustries || '',
      accentColor: template.accentColor || '#0d9488',
      sidebarBgColor: style.sidebarBgColor || '#0A7567',
      layout: style.layout || 'sidebar-left',
      sortOrder: Number(template.sortOrder || 0),
      isActive: template.isActive ?? true,
    });

    setThumbnail(null);
    setTemplateFile(null);
    setMsg('Editing template. Update the values and save.');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="at-shell">
      <header className="at-header">
        <div>
          <p className="at-eyebrow">Template Studio</p>
          <h1 className="at-title">Admin Templates</h1>
          <p className="at-subtitle">
            Create, update, and manage resume templates with metadata, thumbnail, and DOCX source.
          </p>
        </div>

        <div className="at-header-actions">
          <button className="at-ghost-btn" onClick={() => navigate('/admin/dashboard')}>
            Back to dashboard
          </button>
          <button className="at-ghost-btn danger" onClick={logout}>
            Logout
          </button>
        </div>
      </header>

      <main className="at-layout">
        <section className="at-panel at-form-panel">
          <div className="at-panel-head">
            <div>
              <h2>{editing ? 'Edit template' : 'Create template'}</h2>
              <p>Fill all required details and upload the DOCX file used to generate the template.</p>
            </div>
            <span className="at-count">{templates.length} templates</span>
          </div>

          <form className="at-form" onSubmit={save}>
            <div className="at-grid at-grid-2">
              <label className="at-field">
                <span>Template name *</span>
                <input
                  value={form.name}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      name: e.target.value,
                      slug: prev.slug ? prev.slug : buildSlug(e.target.value),
                    }))
                  }
                  placeholder="e.g. Modern Teal"
                />
              </label>

              <label className="at-field">
                <span>Slug *</span>
                <input
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: buildSlug(e.target.value) })}
                  placeholder="e.g. modern-teal"
                />
              </label>
            </div>

            <label className="at-field">
              <span>Description</span>
              <input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Short summary of this template"
              />
            </label>

            <div className="at-grid at-grid-2">
              <label className="at-field">
                <span>Tags</span>
                <input
                  value={form.tags}
                  onChange={(e) => setForm({ ...form, tags: e.target.value })}
                  placeholder="modern, teal, sidebar, ats"
                />
              </label>

              <label className="at-field">
                <span>Recommended industries</span>
                <input
                  value={form.recommendedForIndustries}
                  onChange={(e) =>
                    setForm({ ...form, recommendedForIndustries: e.target.value })
                  }
                  placeholder="Technology, Finance, Consulting"
                />
              </label>
            </div>

            <div className="at-grid at-grid-2">
              <label className="at-field">
                <span>Layout</span>
                <select
                  value={form.layout}
                  onChange={(e) => setForm({ ...form, layout: e.target.value })}
                >
                  <option value="sidebar-left">Sidebar Left</option>
                  <option value="sidebar-right">Sidebar Right</option>
                  <option value="single-column">Single Column</option>
                  <option value="single">Single Column (legacy)</option>
                  <option value="two-column">Two Column</option>
                </select>
              </label>

              <label className="at-field">
                <span>DOCX template {editing ? '' : '*'}</span>
                <input
                  type="file"
                  accept=".doc,.docx"
                  onChange={(e) => setTemplateFile(e.target.files?.[0] || null)}
                />
              </label>
            </div>

            <div className="at-grid at-grid-2">
              <label className="at-field">
                <span>Accent color</span>
                <div className="at-color-row">
                  <input
                    type="color"
                    value={form.accentColor}
                    onChange={(e) => setForm({ ...form, accentColor: e.target.value })}
                  />
                  <input
                    value={form.accentColor}
                    onChange={(e) => setForm({ ...form, accentColor: e.target.value })}
                  />
                </div>
              </label>

              <label className="at-field">
                <span>Sidebar background</span>
                <div className="at-color-row">
                  <input
                    type="color"
                    value={form.sidebarBgColor}
                    onChange={(e) => setForm({ ...form, sidebarBgColor: e.target.value })}
                  />
                  <input
                    value={form.sidebarBgColor}
                    onChange={(e) => setForm({ ...form, sidebarBgColor: e.target.value })}
                  />
                </div>
              </label>
            </div>

            <div className="at-grid at-grid-3">
              <label className="at-field">
                <span>Sort order</span>
                <input
                  type="number"
                  value={form.sortOrder}
                  onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })}
                />
              </label>

              <label className="at-field">
                <span>Status</span>
                <select
                  value={form.isActive ? 'active' : 'inactive'}
                  onChange={(e) => setForm({ ...form, isActive: e.target.value === 'active' })}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </label>

              <label className="at-field">
                <span>Thumbnail</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setThumbnail(e.target.files?.[0] || null)}
                />
              </label>
            </div>

            {thumbnail && <div className="at-file-name">Thumbnail: {thumbnail.name}</div>}
            {templateFile && <div className="at-file-name">Template: {templateFile.name}</div>}

            {msg && (
              <div
                className={`at-msg ${
                  msg.toLowerCase().includes('error') ||
                  msg.toLowerCase().includes('required') ||
                  msg.toLowerCase().includes('could not')
                    ? 'error'
                    : 'success'
                }`}
              >
                {msg}
              </div>
            )}

            <div className="at-actions">
              <button className="at-primary-btn" type="submit" disabled={saving}>
                {saving ? 'Saving...' : editing ? 'Update template' : 'Create template'}
              </button>

              {editing && (
                <button type="button" className="at-secondary-btn" onClick={resetForm}>
                  Cancel edit
                </button>
              )}
            </div>
          </form>
        </section>

        <section className="at-panel">
          <div className="at-panel-head">
            <div>
              <h2>Saved templates</h2>
              <p>Select any template card to edit its configuration.</p>
            </div>
            <button className="at-secondary-btn" onClick={load}>
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="at-empty">Loading templates...</div>
          ) : templates.length === 0 ? (
            <div className="at-empty">No templates yet. Create the first one above.</div>
          ) : (
            <div className="at-template-grid">
              {templates.map((template) => {
                const style = parseStyleConfig(template);
                const layout = style.layout || 'sidebar-left';
                const previewColor = template.accentColor || '#0d9488';

                return (
                  <article key={template.id} className="at-template-card">
                    <button
                      type="button"
                      className="at-template-preview"
                      onClick={() => editTemplate(template)}
                    >
                      {template.thumbnailUrl ? (
                        <img src={`${API_BASE}${template.thumbnailUrl}`} alt={template.name} />
                      ) : (
                        <div
                          className="at-preview-fallback"
                          style={{
                            background: `linear-gradient(135deg, ${previewColor}26, ${previewColor}55)`,
                          }}
                        >
                          <span>📄</span>
                          <span>Preview</span>
                        </div>
                      )}
                    </button>

                    <div className="at-template-body">
                      <div className="at-template-top">
                        <span className="at-dot" style={{ background: previewColor }} />
                        <span className="at-pill">{layout}</span>
                        <span className={`at-status ${template.isActive ? 'active' : 'inactive'}`}>
                          {template.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>

                      <h3>{template.name}</h3>
                      <p className="at-slug">{template.slug || `template-${template.id}`}</p>

                      {!!template.description && (
                        <p className="at-card-desc">{template.description}</p>
                      )}

                      <div className="at-meta">
                        <span>Sort {template.sortOrder ?? 0}</span>
                        <span>{template.accentColor || previewColor}</span>
                        {!!template.recommendedForIndustries && (
                          <span>{template.recommendedForIndustries}</span>
                        )}
                      </div>

                      <div className="at-card-actions">
                        <button
                          type="button"
                          className="at-text-btn"
                          onClick={() => editTemplate(template)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="at-text-btn danger"
                          onClick={() => deleteTemplate(template.id, template.name)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
